import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowDownRight, ArrowUpRight, CalendarPlus, UserPlus, Wrench, FileText, Banknote, Info } from "lucide-react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Input } from "@/components/ui/input";
import { Card, SectionTitle, LoadingState, ErrorState } from "@/components/common";
import { useGarage } from "@/context/GarageContext";
import { useFetch } from "@/lib/useFetch";
import { fmtCents, fmtYmd } from "@/lib/format";
import { t } from "@/i18n";

const PERIODS = ["today", "week", "month", "custom"];

function Change({ pct, testId }) {
  if (pct === null || pct === undefined) return <span className="text-xs text-slate-400" data-testid={testId}>{t("finance.noComparison")}</span>;
  const up = pct >= 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return <span className={`inline-flex items-center text-xs font-semibold ${up ? "text-emerald-700" : "text-red-600"}`} data-testid={testId}><Icon className="h-3.5 w-3.5" />{Math.abs(pct)}% {t("finance.vsPrevious")}</span>;
}

function Kpi({ id, label, value, change, hint }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`kpi-${id}`}>
      <p className="flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">{label}{hint && <Info className="h-3 w-3" title={hint} />}</p>
      <p className="mt-1 font-display text-2xl font-extrabold text-slate-900" data-testid={`kpi-${id}-value`}>{value}</p>
      {change !== undefined && <Change pct={change} testId={`kpi-${id}-change`} />}
      {hint && <p className="mt-1 text-[11px] leading-snug text-slate-500">{hint}</p>}
    </div>
  );
}

export function FinanceBlock() {
  const { garage } = useGarage();
  const cur = garage.currency;
  const [period, setPeriod] = useState("month");
  const [range, setRange] = useState({ start: "", end: "" });
  const params = period === "custom" ? { period, ...range } : { period };
  const ready = period !== "custom" || (range.start && range.end);
  const { data, error, loading, reload } = useFetch(ready ? "/garage/finance" : null, params);
  const k = data?.kpis;
  const d = data?.definitions || {};
  return (
    <Card testId="finance-block">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <SectionTitle hint={data && t("finance.periodLabel", { start: fmtYmd(data.period.start), end: fmtYmd(data.period.end), pstart: fmtYmd(data.previous.start), pend: fmtYmd(data.previous.end) })}>{t("finance.title")}</SectionTitle>
        <div className="flex flex-wrap items-center gap-2">
          {PERIODS.map((p) => <button key={p} type="button" onClick={() => setPeriod(p)} data-testid={`finance-period-${p}`}
            className={`rounded-full border px-3 py-1 text-xs font-semibold ${period === p ? "border-[#0A2540] bg-[#0A2540] text-white" : "border-slate-200 bg-white text-slate-600"}`}>{t(`finance.periods.${p}`)}</button>)}
          {period === "custom" && <>
            <Input type="date" className="h-8 w-40" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="finance-start" />
            <Input type="date" className="h-8 w-40" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="finance-end" />
          </>}
        </div>
      </div>
      {!ready && <p className="text-sm text-slate-500">{t("finance.pickDates")}</p>}
      {ready && loading && !data && <LoadingState />}
      {error && <ErrorState message={error} onRetry={reload} />}
      {k && <>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          <Kpi id="sales" label={t("finance.sales")} value={fmtCents(k.sales_cents, cur)} change={data.change.sales} hint={d.sales} />
          <Kpi id="collections" label={t("finance.collections")} value={fmtCents(k.collections_cents, cur)} change={data.change.collections} hint={d.collections} />
          <Kpi id="outstanding" label={t("finance.outstanding")} value={fmtCents(k.outstanding_cents, cur)} hint={d.outstanding} />
          <Kpi id="jobs" label={t("finance.completedJobs")} value={k.completed_jobs} change={data.change.completed_jobs} />
          <Kpi id="avg-ticket" label={t("finance.avgTicket")} value={fmtCents(k.avg_ticket_cents, cur)} change={data.change.avg_ticket} hint={`${d.avg_ticket} (${k.invoice_count})`} />
          <Kpi id="margin" label={t("finance.margin")} hint={d.margin}
            value={k.margin.complete ? `${fmtCents(k.margin.margin_cents, cur)} · ${k.margin.margin_pct}%` : k.invoice_count ? t("finance.marginIncomplete", { n: k.margin.missing_lines }) : "—"} />
        </div>
        <p className="mt-3 rounded-lg bg-slate-50 p-3 text-xs text-slate-600" data-testid="finance-quotes-accepted">{t("finance.quotesAccepted", { n: k.quotes_accepted_count, amount: fmtCents(k.quotes_accepted_cents, cur) })}</p>
        <div className="mt-4 h-60" data-testid="finance-chart">
          <ResponsiveContainer width="100%" height={240} minWidth={0}>
            <BarChart data={data.series.map((s) => ({ ...s, sales: s.sales_cents / 100, collections: s.collections_cents / 100 }))}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fontSize: 11 }} width={60} />
              <Tooltip formatter={(v) => fmtCents(Math.round(v * 100), cur)} />
              <Legend />
              <Bar dataKey="sales" name={t("finance.salesShort")} fill="#0A2540" radius={[3, 3, 0, 0]} />
              <Bar dataKey="collections" name={t("finance.collectionsShort")} fill="#0D9488" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </>}
    </Card>
  );
}

export function UnpaidCard() {
  const { garage } = useGarage();
  const { data } = useFetch("/garage/finance", { period: "today" });
  if (!data) return null;
  return (
    <Link to="/pro/invoices?status=unpaid" className="gf-card block p-5 transition-shadow hover:shadow-md" data-testid="unpaid-invoices-card">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{t("finance.unpaid")}</p>
      <p className="mt-1 font-display text-3xl font-extrabold" data-testid="unpaid-invoices-count">{data.unpaid.count}</p>
      <p className="text-sm text-slate-600">{fmtCents(data.unpaid.balance_cents, garage.currency)}{data.unpaid.overdue_count > 0 && <span className="ml-2 font-semibold text-red-600">{t("finance.overdue", { n: data.unpaid.overdue_count })}</span>}</p>
    </Link>
  );
}

export function QuickActions() {
  const { hasFeature, isManager } = useGarage();
  const items = [
    { k: "appointment", to: "/pro/planning?new=1", icon: CalendarPlus },
    { k: "customer", to: "/pro/customers?new=1", icon: UserPlus },
    { k: "job", to: "/pro/jobs?new=1", icon: Wrench },
    hasFeature("quotes_and_invoices") && { k: "quote", to: "/pro/quotes?new=1", icon: FileText },
    hasFeature("quotes_and_invoices") && isManager && { k: "payment", to: "/pro/invoices?status=unpaid&pay=1", icon: Banknote },
  ].filter(Boolean);
  return (
    <div className="flex flex-wrap gap-2" data-testid="quick-actions">
      {items.map(({ k, to, icon: Icon }) => (
        <Link key={k} to={to} data-testid={`quick-${k}`} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-800 transition-colors hover:border-[#0A2540]">
          <Icon className="h-4 w-4 text-teal-600" /> {t(`finance.quick.${k}`)}
        </Link>
      ))}
    </div>
  );
}
